import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HintAnimation } from './hint-motion';

/**
 * Spielt einen Hinweis-Film (`HintAnimation`) im Loop, skaliert auf `width`.
 *
 * Die Schleife läuft außerhalb der Angular-Zone (rAF setzt nur Styles), steht
 * bei verborgenem Tab und zeigt bei `prefers-reduced-motion` das Standbild
 * `stillAt` — das erklärt den Hinweis allein.
 *
 * Wechselt `animation` bei stehender Box (Hinweis 1 erledigt → Hinweis 2),
 * wird der neue Film eingebaut und neu gestartet — sonst malte die Schleife
 * in ein DOM, das der neue Film nie gebaut hat, und stand nach dem ersten Bild.
 */
@Component({
  selector: 'app-hint-film',
  template: `<div class="frame" [style.width.px]="width" [style.height.px]="height">
    <div #stage class="stage" [style.transform]="'scale(' + scale + ')'"
      [style.width.px]="animation.width" [style.height.px]="animation.height"></div>
  </div>`,
  styles: [
    `
      :host { display: block; }
      .frame { position: relative; overflow: hidden; border-radius: 14px; }
      .stage { position: absolute; left: 0; top: 0; transform-origin: 0 0; pointer-events: none; }
    `,
  ],
  host: { role: 'img' },
})
export class HintFilmComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) animation!: HintAnimation;
  /** Anzeigebreite in px; die Höhe folgt dem Seitenverhältnis der Bühne. */
  @Input() width = 304;

  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLElement>;

  private raf = 0;
  private start = 0;
  private viewReady = false;
  private readonly onVisibility = () => this.syncRunning();
  private readonly reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(private zone: NgZone) {}

  get scale(): number {
    return this.width / this.animation.width;
  }

  get height(): number {
    return Math.round(this.animation.height * this.scale);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    this.play();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['animation'] && this.viewReady) this.play();
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    cancelAnimationFrame(this.raf);
  }

  /** Baut den aktuellen Film in die Bühne und startet ihn von vorn. */
  private play(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.animation.mount(this.stageRef.nativeElement);
    if (this.reducedMotion) {
      this.animation.render(this.animation.stillAt);
      return;
    }
    this.animation.render(0);
    this.syncRunning();
  }

  private syncRunning(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (document.hidden || this.reducedMotion) return;
    this.start = performance.now();
    this.zone.runOutsideAngular(() => {
      const tick = (now: number) => {
        // Nächstes Bild zuerst anmelden: ein Fehler in einem Bild hält den
        // Film nicht für immer an.
        this.raf = requestAnimationFrame(tick);
        const t = ((now - this.start) / 1000) % this.animation.playbackSeconds;
        this.animation.render(t);
      };
      this.raf = requestAnimationFrame(tick);
    });
  }
}
